// scheduler-red-window-rule.ts
//
// Phase 06j (F-48): the "RED window". A testcase leaves its RED tests on the
// shared mission branch; the controller's recipe runs the whole suite. Until the
// implement/fix that turns those tests green is done, any OTHER implement/fix/
// test verifies against a red suite and is refused `verification_recipe_exit_
// nonzero` (dogfood 9: T-0003 ran after testcase T-0004). Pure — no I/O; keyed
// by `command_adapter` (controller-assigned), never by worker-proposable `kind`.
//
//   green(X)  = nearest GREENING forward from testcase X (each branch stops at
//               the first implement/fix — not transitive, so a window closes as
//               soon as the direct implement is done). X anchored ⇔ green ≠ ∅.
//   window(X) open ⇔ X done ∧ anchored ∧ ¬(∃Y∈green done ∧ every Y done/RETIRED)
//               (every Y retired ⇒ stays open: "RED orphan").
//   cluster(X) = green(X) ∪ testcases that are ancestors of some Y ∈ green(X).
//
// Filter over the ready tier-2 tickets, in this order (each removal recorded in
// `held`): (ii) open windows ⇒ only tickets in ∩ cluster pass (∩ = ∅ with ≥ 2
// windows ⇒ `disjoint_windows`, no fallback); (i-b) an anchored testcase waits
// while a live RED_GATED ancestor of its green — outside its own descendants and
// cluster — is not done; (i-a) anchored testcases yield to any other candidate
// (never empties the list, not recorded). Unanchored testcases (edge-less
// slices) keep the legacy kind order.
import type { Ticket } from "./schemas/ticket-schema.js";
import { ancestorsOf, dependentsIndex, descendantsOf } from "./ticket-dag-reachability.js";

export const TESTCASE_ADAPTER_IDS: readonly string[] = ["testcase-v1"];
export const GREENING_ADAPTER_IDS: readonly string[] = ["implement-v1", "fix-v1"];
export const RED_GATED_ADAPTER_IDS: readonly string[] = [...GREENING_ADAPTER_IDS, "test-v1"];
// Local on purpose: g2-plan-readiness.ts's copy drags the dispatch guard into
// the scheduler (scheduler.ts header: the scheduler stays free of that layer).
const RETIRED = new Set(["cancelled", "superseded"]);

export type HeldCause = "window" | "disjoint_windows" | "prerequisite";
export interface HeldTicket {
  id: string;
  cause: HeldCause;
}
export interface RedWindowFilterResult {
  candidates: Ticket[];
  held: HeldTicket[];
}
export interface Prerequisite {
  green: string;
  waitsOn: string;
}

export interface RedWindowGraph {
  byId: Map<string, Ticket>;
  dependents: Map<string, Ticket[]>;
  status(id: string): string;
  isTestcase(ticket: Ticket): boolean;
  green(x: string): string[];
  cluster(x: string): Set<string>;
  ancestors(id: string): Set<string>;
  openWindows(): string[];
  prerequisites(x: string): Prerequisite[];
}

export function isRetiredStatus(status: string): boolean {
  return RETIRED.has(status);
}

export function buildRedWindowGraph(
  tickets: readonly Ticket[],
  ticketStatus: Readonly<Record<string, string>>,
): RedWindowGraph {
  const byId = new Map(tickets.map((t) => [t.id, t]));
  const dependents = dependentsIndex(tickets);
  const status = (id: string) => ticketStatus[id] ?? "todo";
  const adapterIn = (id: string, set: readonly string[]) =>
    set.includes(byId.get(id)?.command_adapter ?? "");
  const memo = <V>(fn: (key: string) => V) => {
    const cache = new Map<string, V>();
    return (key: string) => {
      if (!cache.has(key)) cache.set(key, fn(key));
      return cache.get(key) as V;
    };
  };
  const ancestors = memo((id) => ancestorsOf(id, byId));
  const green = memo((x) => {
    const found: string[] = [];
    const seen = new Set<string>();
    const queue = [...(dependents.get(x) ?? [])];
    while (queue.length > 0) {
      const next = queue.shift() as Ticket;
      if (seen.has(next.id)) continue;
      seen.add(next.id);
      if (GREENING_ADAPTER_IDS.includes(next.command_adapter)) found.push(next.id);
      else queue.push(...(dependents.get(next.id) ?? []));
    }
    return found.sort();
  });
  const cluster = memo((x) => {
    const members = new Set(green(x));
    for (const y of green(x)) {
      for (const a of ancestors(y)) if (adapterIn(a, TESTCASE_ADAPTER_IDS)) members.add(a);
    }
    return members;
  });
  const isTestcase = (t: Ticket) => TESTCASE_ADAPTER_IDS.includes(t.command_adapter);
  const isOpen = (x: Ticket) => {
    const ys = green(x.id);
    if (!isTestcase(x) || status(x.id) !== "done" || ys.length === 0) return false;
    const settled = ys.every((y) => status(y) === "done" || RETIRED.has(status(y)));
    return !(ys.some((y) => status(y) === "done") && settled);
  };
  const prerequisites = memo((x) => {
    const own = descendantsOf(x, dependents);
    const found: Prerequisite[] = [];
    for (const y of green(x)) {
      for (const n of [...ancestors(y)].sort()) {
        const live = status(n) !== "done" && !RETIRED.has(status(n));
        if (live && adapterIn(n, RED_GATED_ADAPTER_IDS) && !own.has(n) && !cluster(x).has(n))
          found.push({ green: y, waitsOn: n });
      }
    }
    return found;
  });
  return {
    byId,
    dependents,
    status,
    isTestcase,
    green,
    cluster,
    ancestors,
    openWindows: () =>
      tickets
        .filter(isOpen)
        .map((t) => t.id)
        .sort(),
    prerequisites,
  };
}

function intersectClusters(graph: RedWindowGraph, windows: string[]): Set<string> {
  const [first, ...rest] = windows.map((w) => graph.cluster(w));
  return new Set([...(first ?? [])].filter((id) => rest.every((c) => c.has(id))));
}

/** Rules (ii) → (i-b) → (i-a) over the ready tier-2 tickets; see header. */
export function filterRedWindowCandidates(
  readyTier2: readonly Ticket[],
  tickets: readonly Ticket[],
  ticketStatus: Readonly<Record<string, string>>,
): RedWindowFilterResult {
  const graph = buildRedWindowGraph(tickets, ticketStatus);
  const gated = (t: Ticket) =>
    graph.isTestcase(t) || RED_GATED_ADAPTER_IDS.includes(t.command_adapter);
  const held: HeldTicket[] = [];
  let candidates = [...readyTier2];
  const windows = graph.openWindows();
  if (windows.length > 0) {
    const allowed = intersectClusters(graph, windows);
    const cause: HeldCause =
      windows.length >= 2 && allowed.size === 0 ? "disjoint_windows" : "window";
    candidates = candidates.filter((t) => {
      if (!gated(t) || allowed.has(t.id)) return true;
      held.push({ id: t.id, cause });
      return false;
    });
  }
  candidates = candidates.filter((t) => {
    if (!graph.isTestcase(t) || graph.prerequisites(t.id).length === 0) return true;
    held.push({ id: t.id, cause: "prerequisite" });
    return false;
  });
  const anchoredTestcase = (t: Ticket) => graph.isTestcase(t) && graph.green(t.id).length > 0;
  const others = candidates.filter((t) => !anchoredTestcase(t));
  return { candidates: others.length > 0 ? others : candidates, held };
}
